import type { Size } from '@sorrem/shared';
import type { SkillTreeRgb } from './skill-tree-palette';

/** Caps a single frame's step, so a backgrounded tab doesn't jump an animation on return. */
export const MAX_FRAME_SECONDS = 0.1;

/** One stop of a soft dot's radial falloff: its opacity `offset` of the way from the center to the edge. */
export interface SoftDotStop {
    readonly offset: number;
    readonly alpha: number;
}

/** A soft dot's gradient stops as canvas colors. */
export function softDotColorStops(color: SkillTreeRgb, stops: readonly SoftDotStop[]): { offset: number; color: string }[] {
    const [r, g, b] = color;
    return stops.map((stop) => ({ offset: stop.offset, color: `rgba(${r}, ${g}, ${b}, ${stop.alpha})` }));
}

/**
 * A soft round dot in `color`, `size` canvas pixels across, fading from its
 * center to nothing at its edge along `stops`. The skill tree's canvas
 * layers draw one per color once, then stamp it for every mote and spark.
 */
export function createSoftDotSprite(color: SkillTreeRgb, size: number, stops: readonly SoftDotStop[]): HTMLCanvasElement {
    const sprite = document.createElement('canvas');
    sprite.width = size;
    sprite.height = size;
    const context = sprite.getContext('2d');
    if (!context) return sprite;
    const center = size / 2;
    const gradient = context.createRadialGradient(center, center, 0, center, center, center);
    for (const stop of softDotColorStops(color, stops)) gradient.addColorStop(stop.offset, stop.color);
    context.fillStyle = gradient;
    context.fillRect(0, 0, size, size);
    return sprite;
}

/** A canvas sized to its viewport in device pixels, ready to draw. */
export interface FittedCanvas {
    readonly context: CanvasRenderingContext2D;
    readonly pixelRatio: number;
    /** The canvas's size in device pixels. */
    readonly width: number;
    readonly height: number;
}

/** Resizes `canvas` to cover `viewport` (CSS pixels) at the screen's pixel ratio; null without a 2D context. */
export function fitCanvasToViewport(canvas: HTMLCanvasElement, viewport: Size): FittedCanvas | null {
    const context = canvas.getContext('2d');
    if (!context) return null;
    const pixelRatio = window.devicePixelRatio || 1;
    const width = Math.round(viewport.width * pixelRatio);
    const height = Math.round(viewport.height * pixelRatio);
    if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
    }
    return { context, pixelRatio, width, height };
}
